import { Dialog } from '@base-ui/react/dialog';
import { Download, Share2, X } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { canShareFiles, downloadBlob, shareFiles } from '../export/save.ts';
import { errorMessage, toast } from '../state/feedback.ts';
import { Button } from '../ui/Button.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import type { GeneratedQuote } from './generate.ts';

/** Shows the generated PDF before it is downloaded or shared. */
export function QuotePreview({ quote, onClose }: { quote: GeneratedQuote | null; onClose: () => void }) {
  const blob = useMemo(
    () => (quote ? new Blob([quote.bytes as Uint8Array<ArrayBuffer>], { type: 'application/pdf' }) : null),
    [quote],
  );
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => () => void (url && URL.revokeObjectURL(url)), [url]);
  // Read once here: the compiler turns `quote!.fileName` inside handlers into a render-time read.
  const fileName = quote?.fileName ?? 'Quote.pdf';
  return (
    <Dialog.Root open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-scrim" />
        <Dialog.Popup className="fixed inset-2 z-50 flex flex-col overflow-hidden rounded-xl border border-border bg-surface outline-none min-[700px]:inset-8">
          <div className="flex items-center gap-2 border-b border-border p-2">
            <IconButton icon={X} label="Close" onClick={onClose} />
            <Dialog.Title className="t-label min-w-0 flex-1 truncate">{fileName}</Dialog.Title>
            {canShareFiles() ? (
              <Button
                size="sm"
                icon={Share2}
                onClick={async () => {
                  if (!blob) return;
                  try {
                    await shareFiles([new File([blob], fileName, { type: 'application/pdf' })]);
                  } catch (e) {
                    toast(errorMessage(e, 'Sharing is not available on this device.'), 'error');
                  }
                }}
              >
                Share…
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="primary"
              icon={Download}
              onClick={() => {
                if (!blob) return;
                downloadBlob(blob, fileName);
                toast('Downloaded', 'success');
              }}
            >
              Download PDF
            </Button>
          </div>
          {url ? <iframe title="Quote preview" src={url} className="min-h-0 flex-1 bg-white" /> : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
